import React, { FC, ReactNode } from 'react';
import { Dialog } from 'src/components/widgets/dialog';
import { Spinner } from 'src/components/widgets/spinner';
import styled from 'styled-components';
import { SwitchDialogWindow } from './switchDialogWindow';
import { SwitchWizardStep, SwitchWizardStepCount, SwitchWizardStepIndicator } from './switchWizardSteps';

const LoadingContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100%;
    text-align: center;
`;

const LoadingTitle = styled.p`
    margin: 20px 0 0;
`;

const LoadingNote = styled.p`
    margin: 8px 0 0;
    color: #666;
    font-size: 14px;
`;

interface SwitchProgressDialogProps {
    /** The headline under the spinner. */
    title: ReactNode;
    note: ReactNode;
    /** Second note line — only some call sites have one, so it stays an explicit slot rather than a list. */
    secondaryNote?: ReactNode;
    showCloseButton: boolean;
    /** Which wizard stage this spinner belongs to. When set, the shared step indicator is shown above the spinner. */
    step?: SwitchWizardStep;
    stepCount?: SwitchWizardStepCount;
}

/**
 * The spinner-plus-note shell used by every long-running step of the switch flow.
 *
 * `note` and `secondaryNote` are positional slots rather than an array on purpose: an array would need keys,
 * turning static children into a keyed list and changing how React reconciles the dialog.
 */
const SwitchProgressDialog: FC<SwitchProgressDialogProps> = ({ title, note, secondaryNote, showCloseButton, step, stepCount }) => (
    <Dialog
        showCloseButton={showCloseButton}
        windowDecorator={SwitchDialogWindow}
        titleHtml={step !== undefined ? <SwitchWizardStepIndicator step={step} stepCount={stepCount} /> : undefined}
        buttons={[]}
    >
        <LoadingContent>
            <Spinner />
            <LoadingTitle>{title}</LoadingTitle>
            <LoadingNote>{note}</LoadingNote>
            {secondaryNote !== undefined && <LoadingNote>{secondaryNote}</LoadingNote>}
        </LoadingContent>
    </Dialog>
);

export default SwitchProgressDialog;
